import { useMemo } from "react"
import {
	IconCircleCheckOutline18 as CheckCircle,
	IconLink2SlashOutline18 as Link2Off,
	IconCircleXmarkOutline18 as XCircle,
} from "nucleo-ui-outline-18"

import type { FilterType } from "./filter-popover-items/types"
import type { SearchableFilterItem } from "@/components/filters"
import { useFilteredSearchItems } from "@/components/filters"
import { SOURCE_OPTIONS } from "@/components/filters/source-options"
import { companyDomain } from "@/lib/companies"
import { useCompanyOptions } from "@/lib/use-entity-options"
import { FILTER_CATEGORIES } from "./contacts-filter"
import { useContactsFilterState } from "./contacts-filter-state"

const CATEGORY_ORDER: FilterType[] = ["Companies", "Subscription", "CreatedBy"]

export function useContactsSearchableItems({
	onCategorySelect,
	query = "",
}: {
	onCategorySelect: (type: FilterType) => void
	/** What the user typed; companies are searched on the server with it. */
	query?: string
}): SearchableFilterItem<FilterType>[] {
	const filterState = useContactsFilterState()
	const { companies } = useCompanyOptions({
		query,
		selectedIds: filterState.companyIds,
	})

	return useMemo((): SearchableFilterItem<FilterType>[] => {
		const items: SearchableFilterItem<FilterType>[] = []

		FILTER_CATEGORIES.forEach((cat) => {
			items.push({
				id: `category-${cat.type}`,
				category: cat.type,
				categoryLabel: cat.label,
				label: cat.label,
				searchText: cat.label,
				Icon: cat.Icon,
				onSelect: () => onCategorySelect(cat.type),
				isSelected: false,
			})
		})

		items.push({
			id: "company-none",
			category: "Companies",
			categoryLabel: "Company",
			label: "No company",
			searchText: "Company No company",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.companyIds.includes(null)) {
					filterState.removeCompany(null)
				} else {
					filterState.addCompany(null)
				}
			},
			isSelected: filterState.companyIds.includes(null),
		})

		companies.forEach((company) => {
			items.push({
				id: `company-${company.id}`,
				category: "Companies",
				categoryLabel: "Company",
				label: company.name,
				secondaryLabel: companyDomain(company) ?? undefined,
				searchText: `Company ${company.name} ${company.domain ?? ""}`,
				onSelect: () => {
					if (filterState.companyIds.includes(company.id)) {
						filterState.removeCompany(company.id)
					} else {
						filterState.addCompany(company.id)
					}
				},
				isSelected: filterState.companyIds.includes(company.id),
			})
		})

		items.push({
			id: "subscription-true",
			category: "Subscription",
			categoryLabel: "Subscription",
			label: "Subscribed",
			searchText: "Subscription Subscribed",
			Icon: CheckCircle,
			onSelect: () => {
				if (filterState.subscribedStates.includes(true)) {
					filterState.removeSubscribedState(true)
				} else {
					filterState.addSubscribedState(true)
				}
			},
			isSelected: filterState.subscribedStates.includes(true),
		})
		items.push({
			id: "subscription-false",
			category: "Subscription",
			categoryLabel: "Subscription",
			label: "Unsubscribed",
			searchText: "Subscription Unsubscribed",
			Icon: XCircle,
			onSelect: () => {
				if (filterState.subscribedStates.includes(false)) {
					filterState.removeSubscribedState(false)
				} else {
					filterState.addSubscribedState(false)
				}
			},
			isSelected: filterState.subscribedStates.includes(false),
		})

		SOURCE_OPTIONS.forEach(({ value, label, Icon }) => {
			items.push({
				id: `source-${value}`,
				category: "CreatedBy",
				categoryLabel: "Synced with",
				label,
				searchText: `Synced with ${label}`,
				Icon,
				onSelect: () => {
					if (filterState.sources.includes(value)) {
						filterState.removeSource(value)
					} else {
						filterState.addSource(value)
					}
				},
				isSelected: filterState.sources.includes(value),
			})
		})

		return items
	}, [companies, filterState, onCategorySelect])
}

export function useFilteredContactsSearchItems(
	globalQuery: string,
	allItems: SearchableFilterItem<FilterType>[],
) {
	return useFilteredSearchItems(globalQuery, allItems, {
		categoryOrder: CATEGORY_ORDER,
	})
}
